import { NetworkStatus } from '@apollo/client'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { ContentContainer } from '../../../components/custom_components/ContentContainer'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import {
  usePossibleYearsAndCompaniesForRankingReport,
  useRankingReportsByCountryAndFilters,
} from '../../../graphql/reports/useQueriesReport'
import { useCollapse } from '../../../routes/collapse-context'
import { CompaniesForRankingReportDto, Country, RankingReportFilterInput } from '../../../types'
import { noFilterOption, useReportStore } from '../ReportStore'
import { RankingReportsInfoBox } from './RankingReportsInfoBox'
import { RankingReportsTable } from './RankingReportsTable'
import { ShowMoreButton } from './ShowMoreButton'

export const RankingReportsView = (): JSX.Element => {
  const { t } = useTranslation()
  const collapsed = useCollapse()
  const [selectedCountry] = useReportStore((state) => [state.selectedCountry])
  const [reportsSelectedRows, setReportsSelectedRows] = useState<string[]>([])

  const { data, error, loading } = usePossibleYearsAndCompaniesForRankingReport(
    selectedCountry.value === noFilterOption.value ? undefined : (selectedCountry.value as Country),
  )

  if (loading || error || !data || !data.findPossibleYearsAndCompaniesForRankingReport) {
    return (
      <ContentContainer collapsed={collapsed}>
        <BackgroundBox r height="withTabs">
          <NewLoadingAndErrors
            isLoading={loading}
            hasNoData={!data || !data.findPossibleYearsAndCompaniesForRankingReport}
            error={error}
            showSpinner
            t={t}
          />
        </BackgroundBox>
      </ContentContainer>
    )
  }
  return (
    <RankingReportsViewContent
      possibleCompanies={data.findPossibleYearsAndCompaniesForRankingReport.possibleCompanies}
      possibleYears={data.findPossibleYearsAndCompaniesForRankingReport.possibleYears}
      reportsSelectedRows={reportsSelectedRows}
      setReportsSelectedRows={setReportsSelectedRows}
    />
  )
}

export const RankingReportsViewContent: FC<{
  possibleCompanies: CompaniesForRankingReportDto[]
  possibleYears: number[]
  reportsSelectedRows: string[]
  setReportsSelectedRows: (value: string[]) => void
}> = ({ possibleYears, possibleCompanies, reportsSelectedRows, setReportsSelectedRows }) => {
  const [countries, selectedCountry] = useReportStore((state) => [state.countries, state.selectedCountry], shallow)
  const [rankingReportsFilters, setRankingReportsFilters] = useState<RankingReportFilterInput>({ company: undefined, year: undefined })

  useEffect(() => {
    setReportsSelectedRows([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingReportsFilters])

  const { data, error, loading, fetchMore, networkStatus } = useRankingReportsByCountryAndFilters(
    selectedCountry.value === noFilterOption.value ? (countries as Country[]) : [selectedCountry.value as Country],
    rankingReportsFilters,
    {},
  )

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const showMoreVisible = !!data?.getRankingReportsByCountryAndFilters.hasNextPage && (!loading || isLoadingMore)

  const loadMore = () => {
    if (data && data.getRankingReportsByCountryAndFilters.hasNextPage) {
      fetchMore({
        variables: {
          pagination: {
            cursorReportId: data.getRankingReportsByCountryAndFilters.endCursorReportId,
            cursorReportDate: data.getRankingReportsByCountryAndFilters.endCursorReportDate,
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousEdges = previousResult.getRankingReportsByCountryAndFilters.edges
          const fetchMoreEdges = fetchMoreResult.getRankingReportsByCountryAndFilters.edges

          fetchMoreResult.getRankingReportsByCountryAndFilters.edges = [...previousEdges, ...fetchMoreEdges]

          return { ...fetchMoreResult }
        },
      })
    }
  }

  return (
    <>
      <RankingReportsInfoBox
        isLoading={loading}
        counter={!!data ? data.getRankingReportsByCountryAndFilters.edges.length : undefined}
        reportsSelectedRows={reportsSelectedRows}
        setReportsSelectedRows={setReportsSelectedRows}
        rankingReportsFilters={rankingReportsFilters}
        setRankingReportsFilters={setRankingReportsFilters}
        possibleCompanies={possibleCompanies}
        possibleYears={possibleYears}
      />

      <RankingReportsTable
        rankingReportsFilters={rankingReportsFilters}
        reportsSelectedRows={reportsSelectedRows}
        setReportsSelectedRows={setReportsSelectedRows}
        reports={data ? data.getRankingReportsByCountryAndFilters.edges : undefined}
        isLoading={loading}
        isLoadingMore={isLoadingMore}
        error={error}
        showMoreButtonVisible={showMoreVisible}
      />

      <ShowMoreButton visible={showMoreVisible} loadingMore={isLoadingMore} onLoadMore={loadMore} />
    </>
  )
}
